
import {
    defineComponent,
    onBeforeMount,
    onBeforeUnmount,
    PropType,
    reactive,
    Ref,
    ref,
    watch,
} from 'vue'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// module components
import SendersSparkListItem from '@/modules/CreateOrderModule/components/SendersSparkListItem/index.vue'
import TemplatesSparkActions from '@/modules/CreateOrderModule/components/TemplatesSparkActions/index.vue'

// types
import { SenderT } from '@/core/types/senders.types'
import { CreateSenderSparkPayload, SenderSpark } from '@/new-services/types/orders'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import ordersAPI from '@/new-services/orders.service'

// composable
import { useDebounce } from 'vue-composable'
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        Loader,
        TheButton,
        TheInput,
        SendersSparkListItem,
        TemplatesSparkActions,
    },
    props: {
        clientId: {
            type: Number,
            default: null,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        selectedSender: {
            type: Object as PropType<SenderT>,
            default: null,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { actions_state, startCreate, startUpdate } = useActions<SenderT>()
        const notifications = useNotifications()

        const state = reactive({
            sender: null as SenderT | null,
            search: null as string | null,
        })

        const data_state = reactive({
            senders: new DataState<SenderSpark>({}),
        })

        //

        const loadSenders = async (page: number, search?: string) => {
            if (!props.opened) return

            data_state.senders.pagination.current_page = page

            try {
                data_state.senders.is_loading = true

                const params = {
                    page,
                    limit: 50,
                    search,
                }

                const response = await ordersAPI.getSendersSpark(params)
                data_state.senders.list = response.data.data
                data_state.senders.pagination.total_pages = response.data.meta.last_page

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        // infinite scroll
        const loadMoreSenders = async (page: number) => {
            if (page > data_state.senders.pagination.total_pages) return
            data_state.senders.pagination.current_page = page

            try {
                data_state.senders.is_loading = true

                const params = {
                    page,
                    limit: 50,
                }

                const response = await ordersAPI.getSendersSpark(params)
                data_state.senders.list = data_state.senders.list.concat(response.data.data)
                data_state.senders.pagination.total_pages = response.data.meta.last_page

                data_state.senders.is_failed = false
                data_state.senders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.senders.failure.catchError(error)

                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_failed = true
                }
            } finally {
                if (!data_state.senders.failure.error_state.is_cancel) {
                    data_state.senders.is_loading = false
                }
            }
        }

        const templatewindow: Ref<HTMLElement | null> = ref(null)

        const handleScroll = () => {
            if (!templatewindow.value || data_state.senders.is_loading) return

            const scrollHeight = Math.floor(templatewindow.value.scrollHeight)
            const scrollTop = Math.floor(templatewindow.value.scrollTop)
            const offsetHeight = Math.floor(templatewindow.value.offsetHeight)

            if (scrollTop >= scrollHeight - offsetHeight) {
                loadMoreSenders(data_state.senders.pagination.current_page + 1)
            }
        }

        const searchSender = useDebounce(async () => {
            data_state.senders.pagination.current_page = 1
            data_state.senders.pagination.total_pages = 1
            data_state.senders.list = []

            await loadSenders(1, state.search || undefined)
        }, 800)

        const onSenderSelect = (sender: SenderT) => {
            state.sender = sender
            emit('submit', { ...sender })
        }

        //

        const emitReset = () => {
            state.sender = null

            data_state.senders.pagination.current_page = 1
            data_state.senders.pagination.total_pages = 1
            data_state.senders.list = []

            loadSenders(1)

            emit('reset')
        }

        //

        const getPayload = (payload: CreateSenderSparkPayload) => {
            return { ...payload, ownerId: props.clientId }
        }

        const onSenderCreate = async (payload: CreateSenderSparkPayload) => {
            try {
                actions_state.is_actions_loading = true

                await ordersAPI.createSenderSpark(getPayload(payload))
                notifications.notify({ title: 'Отправитель создан', type: 'success' })

                await loadSenders(1)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при создании отправителя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSenderUpdate = async (id: number, payload: CreateSenderSparkPayload) => {
            try {
                actions_state.is_actions_loading = true

                await ordersAPI.updateSenderSpark(id, getPayload(payload))
                notifications.notify({ title: 'Отправитель отредактирован', type: 'success' })

                await loadSenders(1)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании отправителя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSenderDeleted = () => {
            loadSenders(data_state.senders.pagination.current_page)
        }

        const preselectSender = () => {
            onSenderSelect(props.selectedSender)
        }

        //

        watch(
            () => props.opened,
            (opened) => {
                if (opened) {
                    loadSenders(1)
                }

                if (!opened) {
                    state.search = null
                    data_state.senders.pagination.current_page = 1
                    data_state.senders.pagination.total_pages = 1
                    data_state.senders.list = []

                    state.sender = null
                }
            }
        )

        watch(
            () => templatewindow.value,
            (templatewindow_newest) => {
                if (templatewindow_newest) {
                    if (!templatewindow.value) return
                    templatewindow.value.addEventListener('scroll', handleScroll)
                }
            }
        )

        onBeforeMount(async () => {
            await loadSenders(1)

            if (props.selectedSender) {
                preselectSender()
            }
        })

        onBeforeUnmount(() => {
            if (!templatewindow.value) return
            templatewindow.value.removeEventListener('scroll', handleScroll)
        })

        return {
            state,
            data_state,
            loadSenders,
            searchSender,
            onSenderSelect,
            emitReset,
            //
            actions_state,
            startCreate,
            startUpdate,
            //
            onSenderCreate,
            onSenderUpdate,
            onSenderDeleted,
            templatewindow,
        }
    },
})
